import { FC, useState } from 'react';

import { FeaturePreviewItem } from '@eneco/custom-components/FeaturePreviewItem';
import { Accordion, Grid, Image, Stack, Text, TileBar, TransitionOpacity } from '@eneco/sparky';
import { useMediaQuery } from '@eneco/sparky/hooks';
import { ChevronRightIcon } from '@eneco/sparky/icons';
import { getIllustration, mapImage, wrap } from '@sitecore/common';
import { FocalPointImage } from '@sitecore/types/lib';
import { FeaturePreviewRendering } from '@sitecore/types/manual/FeaturePreview';
import { Illustrations } from '@sitecore/types/manual/Illustrations';
import { useDebounce } from 'react-use';

const IconIllustration: FC<{ illustration: Illustrations }> = ({ illustration }) => {
  const Illustration = getIllustration(illustration);
  return <Illustration size="small" />;
};

const FeaturePreviewImage: FC<{ image: FocalPointImage }> = ({ image }) => {
  const { src, sources, alt, aspectRatio } = mapImage(image);
  return <Image alt={alt} display="block" width="100%" src={src ?? ''} sources={sources} aspectRatio={aspectRatio} />;
};

const FeaturePreview: FC<{ fields: FeaturePreviewRendering['fields'] }> = ({ fields }) => {
  const [selectedItemId, setSelectedItemId] = useState(fields?.items?.[0]?.id);
  const [currentImage, setCurrentImage] = useState(fields?.items?.[0]?.fields?.image);
  const [newImage, setNewImage] = useState<FocalPointImage | undefined>();
  const [isTransitioning, setTransitioning] = useState(false);
  const isDesktop = useMediaQuery('md');

  useDebounce(
    () => {
      if (newImage) {
        setCurrentImage(newImage);
        setTransitioning(false);
      }
    },
    300,
    [selectedItemId],
  );

  return isDesktop ? (
    <Grid gridTemplateColumns="2fr 3fr" gap="4">
      <Grid.Item>
        <TileBar direction="column">
          {fields?.items?.map(item => (
            <FeaturePreviewItem
              key={item?.id}
              onClick={() => {
                if (selectedItemId === item?.id) return;
                setSelectedItemId(item?.id);
                setNewImage(item?.fields?.image);
                setTransitioning(true);
              }}
              isSelected={item?.id === selectedItemId}>
              <Stack direction="row" alignX="justify" alignY="center">
                <Stack gap="8" direction="row" alignY="center">
                  <IconIllustration illustration={item?.fields?.illustration?.value} />
                  {(item?.fields?.title?.value || item?.fields?.title?.editable) && (
                    <Text weight="bold">{wrap(item?.fields?.title)}</Text>
                  )}
                </Stack>
                <ChevronRightIcon />
              </Stack>
            </FeaturePreviewItem>
          ))}
        </TileBar>
      </Grid.Item>
      {isTransitioning && newImage ? (
        <>
          <Grid.Item gridRow={1} gridColumn={2}>
            <TransitionOpacity in={!isTransitioning}>
              <FeaturePreviewImage image={currentImage} />
            </TransitionOpacity>
          </Grid.Item>
          <Grid.Item gridRow={1} gridColumn={2}>
            <TransitionOpacity in={isTransitioning}>
              <FeaturePreviewImage image={newImage} />
            </TransitionOpacity>
          </Grid.Item>
        </>
      ) : (
        <Grid.Item gridRow={1} gridColumn={2}>
          {wrap(currentImage, <FeaturePreviewImage image={currentImage} />)}
        </Grid.Item>
      )}
    </Grid>
  ) : (
    <Accordion>
      {fields?.items?.map(item => (
        <Accordion.Item
          leftIcon={<IconIllustration illustration={item?.fields?.illustration?.value} />}
          noContentPadding
          hasChevronBg={false}
          key={item.id}
          heading={item?.fields?.title?.value}>
          <FeaturePreviewImage image={item?.fields?.image} />
        </Accordion.Item>
      ))}
    </Accordion>
  );
};

export { FeaturePreview };
export default FeaturePreview;
